import i18n, { type Resource, type ResourceLanguage } from 'i18next';
import Cookie from 'js-cookie';

import { setI18nResourceLoader, setLanguagePreChangeHook } from '../../utils/i18-next';
import { getDefaultLanguageByHostname, isValidLanguage } from '../../utils/text/translation';

declare global {
  interface Window {
    __SV_ENV?: Record<string, string | undefined>;
    __LOCALE_URLS__?: Record<string, string>;
  }
}

const getBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const base = window.__SV_ENV?.BASE_URL;
    if (typeof base === 'string') {
      return base;
    }
  }
  return '/';
};

/**
 * Mirrors GoogleTranslateLanguageDetector.lookup() so we can detect the active language before
 * i18next is initialised (and therefore before the detector plugin runs).
 *
 * Also checks the ?lang= query parameter first so that shared links pre-load the right bundle.
 * An invalid ?lang= value resolves to 'en' so the fallback is deterministic.
 */
const detectActiveLanguage = (): string => {
  // 1. Hash — Google Translate navigation link sets this before the cookie is updated.
  const hashMatch = /^#googtrans\((.+?)\)$/.exec(window.location.hash ?? '');
  if (hashMatch) {
    const lng = decodeURIComponent(hashMatch[1]);
    if (isValidLanguage(lng)) {
      return lng;
    }
  }

  // 2. Cookie — saved user preference; always beats a shared-link ?lang= param.
  const cookieMatch = /^\/.{2}\/(.{2})$/.exec(Cookie.get('googtrans') ?? '');
  if (cookieMatch) {
    const lng = cookieMatch[1];
    return isValidLanguage(lng) ? lng : 'sv';
  }

  // 3. ?lang= — shared-link hint, only reaches here when no cookie exists.
  const urlLang = new URLSearchParams(window.location.search).get('lang');
  if (urlLang !== null) {
    return isValidLanguage(urlLang) ? urlLang : 'en';
  }

  // 4. Hostname default.
  return getDefaultLanguageByHostname();
};

const loadedLanguages = new Set<string>();

const getLocaleUrl = (language: string): string => {
  const hashed = window.__LOCALE_URLS__?.[language];
  return hashed ? `${getBaseUrl()}${hashed}` : `${getBaseUrl()}locales/${language}.json`;
};

const fetchLanguageBundle = async (language: string): Promise<ResourceLanguage> => {
  const response = await fetch(getLocaleUrl(language));
  if (!response.ok) {
    throw new Error(`Failed to load locale bundle for ${language}`);
  }
  return response.json() as Promise<ResourceLanguage>;
};

/**
 * Fetches a language bundle and adds every namespace to the i18next resource store.
 * Called lazily when the user switches to a language not yet loaded.
 */
const addLanguageToI18n = async (language: string): Promise<void> => {
  try {
    const bundle = await fetchLanguageBundle(language);
    loadedLanguages.add(language);
    for (const [namespace, resources] of Object.entries(bundle)) {
      i18n.addResourceBundle(language, namespace, resources, true, true);
    }
  } catch {
    // Silently swallow — i18next will fall back to English for any missing keys.
  }
};

// Lazy-load a language bundle the first time that language is activated.
i18n.on('languageChanged', (lng: string) => {
  if (isValidLanguage(lng) && !loadedLanguages.has(lng)) {
    void addLanguageToI18n(lng);
  }
});

setI18nResourceLoader(async () => {
  const activeLang = detectActiveLanguage();
  const resources: Resource = {};

  const activeBundle = await fetchLanguageBundle(activeLang).catch(() => null);
  if (activeBundle) {
    resources[activeLang] = activeBundle;
    loadedLanguages.add(activeLang);
    return resources;
  }

  // Active language failed to load — fall back to English so the app remains usable.
  if (activeLang !== 'en') {
    const enBundle = await fetchLanguageBundle('en').catch(() => null);
    if (enBundle) {
      resources.en = enBundle;
      loadedLanguages.add('en');
    }
  }

  return resources;
});

// Ensure the language bundle is loaded before i18next switches to it.
setLanguagePreChangeHook(async (lng) => {
  if (isValidLanguage(lng) && !loadedLanguages.has(lng)) {
    await addLanguageToI18n(lng);
  }
});
